import { TrashIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { EmailValue, LoanPartiesEmailTo } from 'components/EmailTo'
import type { CustomInput, InputType } from 'config'
import { Notes } from 'pages/Admin/ConditionsAndTemplates/Notes'
import { IButtonType, INoteButton } from 'pages/Admin/ConditionsAndTemplates/Notes/types'
import { useEffect, useState } from 'react'
import { loanOverviewSectionByLoanNumber } from 'services'
import { Button, Modal } from 'stories/components'
import { InputConvert, InputValidate } from 'utils'
import { RenderInput } from 'utils/RenderInput'
import { setLoanNumber } from 'utils/setLoanNumber'

import { INote, NotePageType } from './types'

export const defaultInputs = (loanNumber: number): Record<string, InputType> => {
  return {
    subject: {
      inputType: 'text',
      title: 'Subject',
      span: 2,
    },
    content: {
      inputType: 'textarea',
      type: 'text',
      title: 'Note',
      value: '',
      span: 2,
      required: true,
    },
    notifiers: {
      inputType: 'custom',
      title: 'Send Notification To',
      span: 'full',
    },
    files: {
      inputType: 'filetable',
      title: 'Files',
      required: false,
      acceptFileTypes: '.pdf, .msg, .xls, .xlsx',
      multiple: true,
      span: 'full',
      showCategory: false,
      showStatus: false,
      filePath: `loan/${loanNumber}/notes`,
    },
  }
}

export const AddNote = ({
  index,
  item,
  onClose,
  onUpdate,
  isEmail = false,
  replyData = null,
  loanNumber: _loanNumber,
  notePageType = NotePageType.GLOBAL,
  defaultData = {},
}: {
  index: number
  item: INote | null
  onClose: Function
  onUpdate: Function
  isEmail?: boolean
  replyData?: INote | null
  loanNumber?: number
  notePageType?: NotePageType
  defaultData?: Record<string, any>
}) => {
  const loanNumber = _loanNumber || setLoanNumber()
  const [loading, setLoading] = useState(false)
  const [inputs, setInputs] = useState(defaultInputs(loanNumber))
  const [notifiers, setNotifiers] = useState<EmailValue>({})
  const [links, setLinks] = useState<INoteButton[]>([])
  const [showError, setShowError] = useState(false)
  const [showTemplate, setShowTemplate] = useState(false)

  useEffect(() => {
    if (item) {
      const newInputs = cloneDeep(inputs)
      newInputs.subject.value = item.subject
      newInputs.content.value = item.content
      newInputs.files.value = item.files
      setInputs(newInputs)
      setNotifiers(item.notifiers)
      setLinks((item as any).links || [])
    } else {
      if (replyData) return
      const newInputs = cloneDeep(inputs)
      if (defaultData.files) newInputs.files.value = defaultData.files
      loanOverviewSectionByLoanNumber(loanNumber).then((detail) => {
        newInputs.subject.value = `${detail.byteproFileName || detail.loanNumber} | ${detail.entityTitle} | ${
          detail.borrowerName
        } | ${detail.propertyAddress}`
        setInputs(newInputs)
      })
    }
  }, [])

  useEffect(() => {
    if (!replyData) return
    const { noteBy, subject } = replyData
    const splits = noteBy.split(' - ')
    const hasName = splits.length > 1
    const email = hasName ? splits[1] : splits[0]

    const temp: any = cloneDeep(notifiers)
    setNotifiers({ ...temp, [email]: 'to' })

    const newInputs = cloneDeep(inputs)
    newInputs.subject.value = subject
    setInputs(newInputs)
  }, [replyData])

  const onChange = (key: string, value: string) => {
    let newInputs = cloneDeep(inputs)
    value = InputConvert(newInputs[key], value)
    newInputs[key].error = InputValidate({ ...newInputs[key], value })
    newInputs[key].value = value
    setInputs(newInputs)
  }

  const addLink = () => {
    const temp = cloneDeep(links)
    temp.push({ label: '', url: '', type: 'button' })
    setLinks(temp)
  }

  const onChangeLink = (key: string, value: any, index: number) => {
    const temp = cloneDeep(links)
    ;(temp as any)[index][key] = value
    setLinks(temp)
  }

  const removeLink = (index: number) => {
    const temp = cloneDeep(links)
    if (temp.length) temp.splice(index, 1)
    setLinks(temp)
  }

  const onSubmit = async () => {
    let hasError = false

    let newInputs = cloneDeep(inputs)
    const data: Record<string, any> = {}
    for (const key in inputs) {
      newInputs[key].error = InputValidate(newInputs[key])
      data[key] = newInputs[key].value
      if (newInputs[key].error) hasError = true
    }
    if (isEmail && !Object.values(notifiers).includes('to')) {
      newInputs.notifiers.error = 'Please set "To" of Notification Emails'
      hasError = true
    }
    if (isEmail) {
      links.map((linkItem) => {
        if (!linkItem.label || !linkItem.url) hasError = true
      })
    }
    setInputs(newInputs)
    setShowError(true)
    if (hasError) return

    setLoading(true)
    onUpdate(data.subject, data.content, data.files, notifiers, isEmail, replyData, notePageType, links)
  }

  const onChooseTemplate = (template: Record<string, any>) => {
    const temp = cloneDeep(inputs)
    temp.content.value = template.notes
    setInputs(temp)
    setLinks(template.links || [])
    setShowTemplate(false)
  }

  const renderNotifier = () => {
    const { error } = inputs.notifiers
    return (
      <div className="w-full">
        <RenderInput input={{ inputType: 'section', title: 'Send Notification To' }} Key="title" onChange={() => {}} />
        <LoanPartiesEmailTo loanNumber={loanNumber} value={notifiers} data={[]} onChange={setNotifiers} loadParties />
        {error && <p className={`peer-invalid:visible text-rose-700 text-[13px] pt-[1px] pl-1`}>{error}</p>}
      </div>
    )
  }

  const renderLinks = () => {
    return links?.map((link: Record<string, any>, index: number) => {
      const input: InputType = {
        inputType: 'text',
        title: ``,
        value: link.url,
        prefix: 'Link:',
        indent: 12,
        showLinkDir: true,
      }

      input.additionalElements = (
        <>
          <input
            className="border-gray-300 border-[1px] px-1"
            value={link.label}
            onChange={(e: any) => onChangeLink('label', e.target.value, index)}
            placeholder={`${IButtonType[link.type]} Label`}
          />
          <select
            name={`button-type-${index}`}
            id={`button-type-${index}`}
            className="py-0 pl-1 pr-6 text-[12.5px] leading-[16px] border-gray-300"
            value={link.type}
            onChange={(e) => onChangeLink('type', e.target.value, index)}
          >
            {Object.keys(IButtonType).map((key) => {
              return (
                <option value={key} key={key}>
                  {IButtonType[key]}
                </option>
              )
            })}
          </select>
          <span className="cursor-pointer text-red-800" onClick={() => removeLink(index)}>
            <TrashIcon className="w-4 h-4" />
          </span>
        </>
      )

      if (showError && !(link.label && link.url)) {
        input.error = `${link.label ? '' : 'Label is required.'} ${link.url ? '' : 'Link is required.'}`
      }

      return (
        <div className={`col-span-2 opacity-100`} key={index}>
          <RenderInput
            input={input}
            Key={`link-${index}`}
            key={`link-${index}`}
            onChange={(key: string, value: any) => onChangeLink('url', value, index)}
          />
        </div>
      )
    })
  }

  return (
    <Modal
      title={item ? `Update Note - No.${index + 1}` : 'Create Email / Note'}
      titleOkay="Confirm"
      loading={loading}
      onClose={() => onClose()}
      onOk={onSubmit}
      childLevel={2}
      isOpen
    >
      <div className="text-gray-900">
        <div className="w-144 grid gap-4 md:grid-cols-2 grid-cols-1 mb-3 mt-2">
          {Object.keys(inputs).map((key, index) => {
            let input = inputs[key]
            if (key == 'notifiers') {
              if (!isEmail) return
              ;(input as CustomInput).render = renderNotifier
            }
            if (key === 'content') {
              input.additionalElements = (
                <span className="text-shade-blue hover:underline cursor-pointer" onClick={() => setShowTemplate(true)}>
                  Choose Template
                </span>
              )
            }
            return (
              <div className={`input md:col-span-${input.span || 1}`} key={index}>
                <RenderInput input={input} Key={key} onChange={onChange} />
              </div>
            )
          })}
          {isEmail && (
            <>
              {renderLinks()}
              <div className="col-span-full">
                <Button link onClick={addLink}>
                  + Add Button & Link
                </Button>
              </div>
            </>
          )}
        </div>
        {showTemplate && (
          <Modal title="Note Template" titleOkay="" onClose={() => setShowTemplate(false)} isOpen>
            <div className="min-w-[600px]">
              <Notes isModal onRawClick={onChooseTemplate} />
            </div>
          </Modal>
        )}
      </div>
    </Modal>
  )
}
